import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Image,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconFileUpload,
  IconGripVertical,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';

import { toApiError } from '../apollo/errors';
import { QueryState } from '../components/QueryState';
import type { CatalogCollection } from '../gql/graphql';
import {
  AdminCatalogDocument,
  AdminDeleteCatalogItemDocument,
  AdminReorderCatalogDocument,
} from '../graphql/operations';
import { COLLECTIONS, hasArtwork, isCatalogCollection, useLabels } from '../labels';
import { CatalogImportModal } from './CatalogImportModal';
import { CatalogItemModal, type CatalogItem } from './CatalogItemModal';

// A 1x1 transparent pixel: `<Image>` needs *something* to fall back to, and a
// broken-image icon next to every moral would be noise, not information.
const BLANK =
  "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3C/svg%3E";

/** Validates the `:collection` segment, so the view below can take it as a type. */
export function CatalogPage() {
  const { collection } = useParams();

  if (!isCatalogCollection(collection)) return <Navigate to="/catalog/characters" replace />;

  // Keyed so switching tabs resets the local order rather than animating the
  // old collection's rows into the new one's.
  return <CatalogCollectionView key={collection} collection={collection} />;
}

function CatalogCollectionView({ collection }: { collection: CatalogCollection }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const labels = useLabels();
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [untranslatedOnly, setUntranslatedOnly] = useState(false);
  // A local copy of the list, so a drag lands instantly instead of after a
  // round trip -- and can be put back if the server refuses it.
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [syncedFrom, setSyncedFrom] = useState<readonly CatalogItem[] | null>(null);

  const { data, loading, error } = useQuery(AdminCatalogDocument, {
    variables: { collection },
    fetchPolicy: 'cache-and-network',
  });

  // Adjusting state during render rather than in an effect: the server's order
  // is the truth, the local copy only leads it by one drag, and React's own
  // guidance for "reset state when the data changes" is exactly this.
  if (data && data.adminCatalog !== syncedFrom) {
    setSyncedFrom(data.adminCatalog);
    setItems(data.adminCatalog);
  }

  // Which rows still have no English name. Filtering is a view over `items`
  // rather than a second list, so a drag still reorders the real thing --
  // except that it cannot, while the filter hides rows: `adminReorderCatalog`
  // wants every id of the table, and the indices of a filtered list do not
  // describe the whole one. So dragging is off until the filter is.
  const visible = untranslatedOnly ? items.filter((item) => !item.labels.en) : items;

  const [reorder, { loading: reordering }] = useMutation(AdminReorderCatalogDocument);
  const [remove] = useMutation(AdminDeleteCatalogItemDocument, {
    refetchQueries: [{ query: AdminCatalogDocument, variables: { collection } }],
    awaitRefetchQueries: true,
  });

  async function onDragEnd(result: DropResult) {
    const to = result.destination?.index;

    if (to === undefined || to === result.source.index) return;

    const next = [...items];
    const [moved] = next.splice(result.source.index, 1);

    if (!moved) return;

    next.splice(to, 0, moved);
    setItems(next);

    try {
      // `adminReorderCatalog` wants every id of the table, exactly once, in the
      // wanted order -- a partial list is refused rather than applied.
      await reorder({ variables: { collection, ids: next.map((item) => item.id) } });
    } catch (thrown) {
      // Put the rows back where the server still thinks they are.
      if (data) setItems(data.adminCatalog);
      notifications.show({
        color: 'red',
        title: t('catalog.reorderFailed'),
        message: toApiError(thrown as ErrorLike)?.message ?? '',
      });
    }
  }

  function confirmDelete(item: CatalogItem) {
    modals.openConfirmModal({
      title: t('catalog.confirmDelete.title'),
      centered: true,
      children: (
        <Text size="sm">
          {t('catalog.confirmDelete.text', { name: item.labels.ru, id: item.id })}
        </Text>
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void remove({ variables: { collection, id: item.id } })
          .then(() => {
            notifications.show({ color: 'teal', message: t('catalog.deleted') });
          })
          .catch((thrown: ErrorLike) => {
            notifications.show({
              color: 'red',
              title: t('catalog.deleteFailed'),
              message: toApiError(thrown)?.message ?? '',
            });
          });
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t('catalog.title')}</Title>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconFileUpload size={16} />}
            onClick={() => {
              setImportOpen(true);
            }}
          >
            {t('catalog.import.action')}
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            {t('catalog.add')}
          </Button>
        </Group>
      </Group>

      <SegmentedControl
        value={collection}
        onChange={(value) => void navigate(`/catalog/${value}`)}
        data={COLLECTIONS.map((value) => ({ value, label: labels.collection(value) }))}
      />

      <QueryState loading={loading && !data} error={error}>
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {untranslatedOnly ? t('catalog.reorderDisabled') : t('catalog.reorderHint')}
          </Text>
          <Switch
            label={t('catalog.untranslatedOnly')}
            checked={untranslatedOnly}
            onChange={(event) => {
              setUntranslatedOnly(event.currentTarget.checked);
            }}
          />
        </Group>

        {/* The two name columns, headed once rather than on every card. The
            spacers stand in for the drag handle and the artwork, so the
            headings sit over the text they describe. */}
        {visible.length > 0 && (
          <Group gap="sm" wrap="nowrap" px="xs">
            <div style={{ width: 18 }} />
            {hasArtwork(collection) && <div style={{ width: 40 }} />}
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ flex: 1, minWidth: 0 }}>
              {t('catalog.nameRu')}
            </Text>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ flex: 1, minWidth: 0 }}>
              {t('catalog.nameEn')}
            </Text>
          </Group>
        )}

        <DragDropContext onDragEnd={(result) => void onDragEnd(result)}>
          <Droppable droppableId="catalog">
            {(droppable) => (
              <Stack
                gap="xs"
                ref={droppable.innerRef}
                {...droppable.droppableProps}
                style={{ opacity: reordering ? 0.7 : 1 }}
              >
                {visible.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={untranslatedOnly}
                  >
                    {(draggable) => (
                      <Card
                        withBorder
                        padding="xs"
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <div
                            {...draggable.dragHandleProps}
                            aria-label={t('catalog.move', { name: item.labels.ru })}
                            style={{
                              display: 'flex',
                              cursor: untranslatedOnly ? 'not-allowed' : 'grab',
                              opacity: untranslatedOnly ? 0.4 : 1,
                            }}
                          >
                            <IconGripVertical size={18} />
                          </div>

                          {hasArtwork(collection) && (
                            <Image
                              src={item.artworkUrl}
                              alt=""
                              h={40}
                              w={40}
                              radius="sm"
                              fit="contain"
                              fallbackSrc={BLANK}
                            />
                          )}

                          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                            <Text fw={500}>{item.labels.ru}</Text>
                            <Text size="xs" c="dimmed">
                              {item.id}
                            </Text>
                          </Stack>

                          {/* The second column: what the app shows under `en`,
                              or the fact that it would fall back to Russian. */}
                          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                            {item.labels.en ? (
                              <Text>{item.labels.en}</Text>
                            ) : (
                              <Badge variant="light" color="gray" size="sm">
                                {t('catalog.noTranslation')}
                              </Badge>
                            )}
                          </Stack>

                          {item.category && (
                            <Badge variant="light" size="sm">
                              {labels.characterCategory(item.category)}
                            </Badge>
                          )}

                          {item.characterType && (
                            <Badge variant="light" color="grape" size="sm">
                              {labels.characterType(item.characterType)}
                            </Badge>
                          )}

                          <Tooltip label={t('common.edit')}>
                            <ActionIcon
                              variant="subtle"
                              aria-label={t('catalog.edit', { name: item.labels.ru })}
                              onClick={() => {
                                setEditing(item);
                                setModalOpen(true);
                              }}
                            >
                              <IconPencil size={18} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip label={t('common.delete')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t('catalog.delete', { name: item.labels.ru })}
                              onClick={() => {
                                confirmDelete(item);
                              }}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {droppable.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>

        {visible.length === 0 && (
          <Text c="dimmed" ta="center" py="md">
            {untranslatedOnly ? t('common.nothingFound') : t('catalog.empty')}
          </Text>
        )}
      </QueryState>

      {modalOpen && (
        <CatalogItemModal
          key={editing?.id ?? 'new'}
          collection={collection}
          item={editing}
          opened={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
        />
      )}

      {importOpen && (
        <CatalogImportModal
          opened={importOpen}
          onClose={() => {
            setImportOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
